export interface Coverage {
  total: number;
  percentage: number;
}

// The share that must never be reached while a single location is still disabled. Rounding to one
// decimal takes 9999/10000 to 100, and a full network beside a non-zero «desactivadas» legend is
// the card contradicting itself — the reader believes the headline, not the legend.
const ALMOST_ALL = 99.9;

// Grouping forced on rather than left at the default: Spanish CLDR sets `minimumGroupingDigits` to
// 2, so four-digit numbers come out bare. These counts sit exactly astride that boundary — a network
// of 9.610 configured locations would render unseparated while the 12.960 beside it is grouped, and
// one card cannot spell the same kind of quantity two ways. `true` resolves to "always".
const countFormatter = new Intl.NumberFormat('es-ES', { useGrouping: true });
const percentageFormatter = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

// `null` when nothing is configured: the table is deny-by-default and only has a row for a location
// somebody has touched, so an empty one has no denominator to divide by. Returning a zero share
// instead would render a truthful-looking `0,0 %` about a network nobody has decided anything for.
export function computeCoverage(enabled: number, disabled: number): Coverage | null {
  const total = enabled + disabled;
  if (total === 0) {
    return null;
  }
  const exact = (enabled / total) * 100;
  const rounded = Math.round(exact * 10) / 10;
  return { total, percentage: disabled > 0 ? Math.min(rounded, ALMOST_ALL) : rounded };
}

export function formatCount(count: number): string {
  return countFormatter.format(count);
}

export function formatPercentage(percentage: number): string {
  return `${percentageFormatter.format(percentage)} %`;
}
